import moment from "moment"
import { ComponentProps, FC, useCallback, useEffect, useState } from "react"
import { IContact, IHallBooking, ILocation } from "src/domain/entities"
import { hallBookingUpdateService } from "src/domain/services/hallBooking/hallBookingUpdateService"
import { useAuth, useContactListService, useLocationListService } from "src/hooks"
import { FormikOnSubmit } from "src/utils/types"
import { HallBookingUpdateDrawerFormView, IHallBookingAddFormFields } from "."

interface Props
	extends Omit<
		ComponentProps<typeof HallBookingUpdateDrawerFormView>,
		| "handleSubmit"
		| "locationList"
		| "contactList"
		| "selectedLocation"
		| "setSelectedLocation"
		| "selectedContact"
		| "setSelectedContact"
	> {
	onSuccess: (item: IHallBooking) => void
}

export const HallBookingUpdateDrawerFormController: FC<Props> = ({
	onSuccess,
	hallBooking,
	...rest
}) => {
	const { token } = useAuth()

	const { locationList, fetchLocationList } = useLocationListService()
	const { contactList, fetchContactList } = useContactListService()

	const [selectedLocation, setSelectedLocation] = useState<ILocation | undefined>(
		hallBooking.location,
	)
	const [selectedContact, setSelectedContact] = useState<IContact | undefined>(
		hallBooking.contact,
	)

	const handleSubmit = useCallback<FormikOnSubmit<IHallBookingAddFormFields>>(
		async (values, { setErrors }) => {
			try {
				const updatedHallBooking = await hallBookingUpdateService(
					{
						id: hallBooking.id,
						update: {
							...values,
							startDate: moment(values.startDate).unix() * 1000,
							endDate: moment(values.endDate).unix() * 1000,
						},
					},
					token,
				)
				onSuccess(updatedHallBooking)
				rest.onClose()
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Something went wrong"
				setErrors({ reason: message })
			}
		},
		[onSuccess, rest, token, hallBooking.id],
	)

	useEffect(() => {
		fetchLocationList()
	}, [fetchLocationList])

	useEffect(() => {
		fetchContactList()
	}, [fetchContactList])

	return (
		<HallBookingUpdateDrawerFormView
			hallBooking={hallBooking}
			handleSubmit={handleSubmit}
			locationList={locationList}
			contactList={contactList}
			selectedLocation={selectedLocation}
			setSelectedLocation={setSelectedLocation}
			selectedContact={selectedContact}
			setSelectedContact={setSelectedContact}
			{...rest}
		/>
	)
}
